import { memo, type ReactNode } from 'react';

/** Vertical stack spacing between field rows inside a share modal section. */
// biome-ignore lint/plugin: this class string is exported and reused by several share modal sections
export const SHARE_MODAL_SECTION_ROWS_CLASS_NAME = 'flex flex-col gap-2';

export type ShareModalRowProps = {
  children: ReactNode;
  label: ReactNode;
  /** Rendered immediately after the label (e.g. upsell icons / tooltips). */
  labelAccessory?: ReactNode;
  /** When provided and the label is a string, renders the label as a `<label htmlFor>` so a control can be clicked via its label. */
  htmlFor?: string;
  'data-testid'?: string;
};

export const ShareModalRow = memo(
  ({ children, label, labelAccessory, htmlFor, 'data-testid': dataTestId }: ShareModalRowProps) => {
    let labelElement: ReactNode;

    if (typeof label === 'string') {
      labelElement = htmlFor ? (
        <label className="min-w-0 ads:text-primary text-14 text-gray-12" htmlFor={htmlFor}>
          {label}
        </label>
      ) : (
        <span className="min-w-0 ads:text-primary text-14 text-gray-12">{label}</span>
      );
    } else {
      labelElement = label;
    }

    return (
      <div className="flex items-center justify-between gap-2" data-testid={dataTestId}>
        <div className="flex min-w-0 items-center gap-2">
          {labelElement}
          {labelAccessory}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">{children}</div>
      </div>
    );
  },
);

ShareModalRow.displayName = 'ShareModalRow';
